import { useLayoutEffect, useRef } from 'react';

/**
 * A ref that always holds the latest value, readable from long-lived callbacks.
 *
 * WHAT IT IS FOR
 *
 * A streamed response outlives the render that started it: the SSE handlers in
 * `useChat` run for seconds, and a plain closure would read whatever the props
 * were when the request went out. Mirroring a prop into a ref lets those
 * callbacks read the current value instead — and, just as importantly, keeps
 * the prop OUT of the enclosing `useCallback`'s dependency array, so the
 * streaming function is not rebuilt underneath an in-flight request.
 *
 * WHY IT IS NOT `ref.current = value` IN THE RENDER BODY
 *
 * That is what this replaces, and it is a purity violation: React may render a
 * component and throw the result away (StrictMode's double render, a Suspense
 * replay, an interrupted concurrent render). An abandoned render that has
 * already written the ref leaves a value from a render that never committed.
 *
 * `useLayoutEffect` runs after the commit but before paint and before any child
 * effect, so a callback fired from an event or a stream sees exactly the same
 * value it would have seen from the assignment — without writing during render.
 *
 * WHEN NOT TO USE IT
 *
 * Only for values a callback needs to READ LATE. State that a turn owns and
 * mutates — the abort controller, the id of the assistant message being
 * streamed into — is genuinely ref-shaped and does not belong here: it has no
 * prop to track, and writing it during an event is the whole point.
 */
export function useLatest<T>(value: T): { readonly current: T } {
  const ref = useRef(value);
  useLayoutEffect(() => {
    ref.current = value;
  }, [value]);
  return ref;
}
